import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import {
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  StyledModalContent,
  TitleText,
} from "./QueryScopeModal";
import StyledText from "../../components/StyledComponents/StyledText";
import { CorporateTheme } from "../../utils/themes";
import { Header } from "../forms/CustomForms/ShareFormModal";
import styled from "styled-components";
import { Button } from "../../components/form/Button";
import CrossIcon from "../../assets/icons/CrossIcon";
import DocumentIcon from "../../assets/icons/document-text";
import { useBulkNotesMutation, useScheduledQueryMutation } from "../../api/searchQueryApiSlice";
import { useToast } from "../../components/toast/ToastContext";
import LoadingCircleIcon from "../../assets/icons/LoadingCircleIcon";
import { SkeletonCell } from "../../components/SkeletonCell";
import StyledInput from "../../components/StyledComponents/StyledInput";
import { useTheme } from "../../middleware/ThemeContext";
import SaveIcon from "../../assets/icons/SaveIcon";

type AssignPrivateNoteModalProps = {
  title?: string;
  subTitle?: string;
  onClose: () => void;
  scope:string;
  ids?: string[]; // or number[] or appropriate type for your IDs
};

const AssignPrivateNoteModal: React.FC<AssignPrivateNoteModalProps> = ({
  title,
  subTitle,
  onClose,
  scope,
  ids,
}) => {
  const {theme} = useTheme();
  const [notes, setNotes] = useState("");
  const [notesError, setNotesError] = useState("");

  const [addBulkNotes, { isLoading: bulkNotesLoading }] =
     useBulkNotesMutation();
  const showToast = useToast();

  const handleSubmit = async () => {
    // Simple validation
    if (!notes.trim()) {
      setNotesError("Notes field is required.");
      return;
    }

    setNotesError(""); // Clear error

    const call = addBulkNotes;

    const val = {
      recordIds: ids && ids?.map(Number),
      scope: scope,
      noteText:notes
    };

    call(val)
      .unwrap()
      .then((res) => {
        if (res?.error) {
          showToast(
            "Action Failed",
            `${"Failed to add private notes"}`,
            "error"
          );

          return;
        }
        showToast(
          ` Private Notes added`,
          `Bulk private notes added successfully`,
          "success"
        );
        onClose();
      });
  };

  return (
    <Modal show={true} onHide={onClose} size="lg" centered>
      <StyledModalContent theme={theme}>
        <ModalHeader>
          <Header>
            <StyledCloseButton onClick={onClose} style={{ color: theme.CorporateTheme.colors.textSecondary }}>×</StyledCloseButton>
            <ModalTitle className="pb-3 pt-1 mt-4">
              <TitleText theme={theme}>{title ?? "Add Private Notes"}</TitleText>
              <StyledText
                fontSize="14px"
                fontWeight={500}
                textColor={theme.CorporateTheme?.colors?.textSecondary}
              >
                {subTitle ??
                  "Enables users to add private notes to several selected records at once via the bulk action option."}
              </StyledText>
            </ModalTitle>
          </Header>
        </ModalHeader>
        <ModalBody>
          <div className="d-flex align-items-center  px-1 py-3 border rounded mt-4 mx-5">
            <div className="p-4 w-100">
              <div className="mb-4">
                <StyledText
                  className=" mb-2"
                  textColor={theme.CorporateTheme?.colors?.headerColor}
                >
                  Selected Records
                </StyledText>
                <StyledText
                  className=" mb-2"
                  fontWeight={500}
                  textColor={theme.CorporateTheme?.colors?.textSecondary}
                >
                  {(ids ?? [])?.length}
                </StyledText>
              </div>

              <div className="mt-4 d-flex flex-column gap-3">
                <label style={{ color: theme.CorporateTheme?.colors?.headerColor }}>
                  Notes
                  <span className="text-danger  mt-4">*</span>
                </label>

                <div className="d-flex flex-column">
                  <StyledInput
                    theme={theme}
                    name="notes"
                    as="textarea"
                    isInvalid={!!notesError}
                    rows={5}
                    value={notes}
                    onChange={(e) => {
                      setNotes(e.target.value);
                      if (notesError) setNotesError(""); // Clear error on type
                    }}
                    onBlur={(e) => {
                      if (!e.target.value.trim())
                        setNotesError("Notes field is required.");
                    }}
                  />
                  {notesError && (
                    <span
                      className="text-danger mt-1"
                      style={{ fontSize: "13px" }}
                    >
                      {notesError}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="d-flex align-items-center justify-content-end mt-3 me-5">
            <div className="d-flex gap-1">
              <Button
                theme={theme}
                type="button"
                className={`cursor-pointer ${bulkNotesLoading ? "class-disable" : ""}`}
                $variant="cancel"
                onClick={onClose}
              >
                <CrossIcon />
                Cancel
              </Button>
              <Button
                theme={theme}
                type="submit"
                $variant="add"
                className={`cursor-ponter ${bulkNotesLoading ? "class-disable" : ""} `}
                onClick={handleSubmit}
              >
                {bulkNotesLoading ? (
                  <>
                    <label>Saving...</label>
                    <LoadingCircleIcon color={theme.CorporateTheme.colors.background} />
                  </>
                ) : (
                  <div
                    className={`d-flex align-items-center gap-1 cursor-pointer`}
                  >
                    <label className="cursor-pointer">
                      <SaveIcon color={theme.CorporateTheme.colors.background} /> Save{" "}
                    </label>
                  </div>
                )}
              </Button>
            </div>
          </div>
        </ModalBody>
        <ModalFooter />
      </StyledModalContent>
    </Modal>
  );
};

export default AssignPrivateNoteModal;

const StyledCloseButton = styled(ModalCloseButton)`
  top: 10px;
`;
const StyledIntervalButton = styled.div`
  cursor: pointer;
  &.active {
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #003366;
    color: #fff;
    height: 33px;
    width: 100%;
    border-radius: 5px;
    padding: 0 10px;
  }
`;
